import { useEffect, useState } from "react";
import { useNavigate, useParams, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save, Cpu, FileText } from "lucide-react";

import { api, formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import Breadcrumbs from "@/components/Breadcrumbs";
import FileUploader from "@/components/FileUploader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { EntityCombobox } from "@/components/ui/EntityCombobox";
import { readBack } from "@/lib/nav";

const EMPTY = {
  site_id: "",
  machine_type_id: "",
  make: "",
  model: "",
  serial: "",
  install_date: "",
  notes: "",
  manual_file_ids: [],
  active: true,
};

export default function MachineForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [params] = useSearchParams();

  // Query-param prefills (captured once, applied lazily so Radix Select
  // sees SelectItems before the value changes).
  const prefillCustomerId = params.get("customer_id") || null;
  const prefillSiteId = params.get("site_id") || null;
  const prefillTypeId = params.get("machine_type_id") || null;
  const backCtx = readBack(params);

  const [form, setForm] = useState({ ...EMPTY });
  const [manuals, setManuals] = useState([]);
  const [sites, setSites] = useState([]);
  const [customerName, setCustomerName] = useState("");
  const [customerHasNoSites, setCustomerHasNoSites] = useState(false);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  // Load sites — scoped to the prefill customer when present, otherwise global.
  useEffect(() => {
    const sitesParams = { active: true, limit: 500 };
    if (prefillCustomerId && !isEdit) sitesParams.customer_id = prefillCustomerId;
    Promise.all([
      api.get("/sites", { params: sitesParams }),
      api.get("/machine-types", { params: { active: true } }),
    ]).then(([{ data: s }, { data: t }]) => {
      const items = s.items || [];
      setSites(items);
      setTypes(t);
      if (prefillCustomerId && !isEdit) setCustomerHasNoSites(items.length === 0);
    }).catch((err) => toast.error(formatApiError(err)));
    // Fetch the customer name for the inline hint.
    if (prefillCustomerId && !isEdit) {
      api.get(`/customers/${prefillCustomerId}`)
        .then(({ data }) => setCustomerName(data?.name || ""))
        .catch(() => {});
    }
  }, [prefillCustomerId, isEdit]);

  // Apply site_id prefill AFTER sites list has landed in state.
  useEffect(() => {
    if (isEdit || !prefillSiteId) return;
    if (sites.some((s) => s.id === prefillSiteId)) {
      setForm((f) => (f.site_id ? f : { ...f, site_id: prefillSiteId }));
    }
  }, [sites, prefillSiteId, isEdit]);

  // Apply machine_type_id prefill AFTER types list has landed in state.
  useEffect(() => {
    if (isEdit || !prefillTypeId) return;
    if (types.some((t) => t.id === prefillTypeId)) {
      setForm((f) => (f.machine_type_id ? f : { ...f, machine_type_id: prefillTypeId }));
    }
  }, [types, prefillTypeId, isEdit]);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { data } = await api.get(`/machines/${id}`);
        setForm({
          site_id: data.site_id || "",
          machine_type_id: data.machine_type_id || "",
          make: data.make || "",
          model: data.model || "",
          serial: data.serial || "",
          install_date: data.install_date || "",
          notes: data.notes || "",
          manual_file_ids: data.manual_file_ids || [],
          active: data.active,
        });
        setManuals(data.manuals || []);
      } catch (err) {
        toast.error(formatApiError(err));
        navigate("/machines", { replace: true });
      } finally { setLoading(false); }
    })();
  }, [id, isEdit, navigate]);

  const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  // Manuals are attached/detached via the API against a saved machine.
  const handleAttach = async (file) => {
    if (!isEdit) {
      // pre-save: just keep locally until save
      update({ manual_file_ids: [...form.manual_file_ids, file.id] });
      setManuals([...manuals, file]);
      return;
    }
    const { data } = await api.post(`/machines/${id}/manuals`, { file_id: file.id });
    update({ manual_file_ids: data.manual_file_ids });
    setManuals(data.manuals);
  };
  const handleDetach = async (fileId) => {
    if (!isEdit) {
      update({ manual_file_ids: form.manual_file_ids.filter((f) => f !== fileId) });
      setManuals(manuals.filter((m) => m.id !== fileId));
      return;
    }
    const { data } = await api.delete(`/machines/${id}/manuals/${fileId}`);
    update({ manual_file_ids: data.manual_file_ids });
    setManuals(data.manuals);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      if (isEdit) {
        const { data } = await api.patch(`/machines/${id}`, form);
        toast.success("Machine updated");
        navigate(backCtx?.to || `/machines/${data.id}`);
      } else {
        const { data } = await api.post("/machines", form);
        toast.success("Machine created");
        navigate(backCtx?.to || `/machines/${data.id}`);
      }
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;

  // Cancel target: explicit back=, else natural parent (site/customer detail) if prefill exists, else index.
  const cancelTo = backCtx?.to
    || (isEdit ? `/machines/${id}` :
        prefillSiteId ? `/sites/${prefillSiteId}?tab=machines` :
        prefillCustomerId ? `/customers/${prefillCustomerId}?tab=machines` :
        "/machines");

  return (
    <div className="p-6 space-y-6" data-testid="machine-form-page">
      <Breadcrumbs
        items={[
          { label: "Machines", to: "/machines" },
          ...(isEdit ? [{ label: `${form.make} ${form.model}`.trim() || "Machine", to: `/machines/${id}` }, { label: "Edit" }] : [{ label: "New" }]),
        ]}
      />
      <PageHeader
        testId="machine-form-header"
        title={isEdit ? `Edit ${form.make} ${form.model}` : "New machine"}
        actions={<Button asChild variant="outline" size="sm" data-testid="machine-form-back"><Link to={cancelTo}><ArrowLeft className="h-4 w-4" /> Back</Link></Button>}
      />

      <form onSubmit={submit} className="space-y-6 max-w-4xl">
        <Card className="rounded-md">
          <CardHeader><CardTitle className="text-sm font-medium flex items-center gap-2"><Cpu className="h-4 w-4 text-primary" /> Details</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="site">Site</Label>
              <EntityCombobox
                items={sites}
                value={form.site_id || ""}
                onChange={(v) => update({ site_id: v })}
                placeholder={customerHasNoSites ? "No sites for this customer" : "Select site"}
                emptyMessage="No sites match."
                disabled={customerHasNoSites}
                testid="machine-form-site-select"
              />
              {customerHasNoSites && prefillCustomerId && (
                <div className="text-xs text-muted-foreground pt-1" data-testid="machine-form-no-sites-hint">
                  <Link to={`/customers/${prefillCustomerId}`} className="underline hover:text-primary" data-testid="machine-form-no-sites-link">
                    Add a site first
                  </Link>
                  {customerName ? ` for ${customerName}.` : "."}
                </div>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="type">Machine type</Label>
              <EntityCombobox
                items={types}
                value={form.machine_type_id || ""}
                onChange={(v) => update({ machine_type_id: v })}
                placeholder="Select type"
                emptyMessage="No types match."
                testid="machine-form-type"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="make">Make</Label>
              <Input id="make" value={form.make} onChange={(e) => update({ make: e.target.value })} data-testid="machine-form-make" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="model">Model</Label>
              <Input id="model" value={form.model} onChange={(e) => update({ model: e.target.value })} data-testid="machine-form-model" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="serial">Serial</Label>
              <Input id="serial" value={form.serial} onChange={(e) => update({ serial: e.target.value })} data-testid="machine-form-serial" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="install">Install date</Label>
              <Input id="install" type="date" value={form.install_date || ""} onChange={(e) => update({ install_date: e.target.value })} data-testid="machine-form-install-date" />
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea id="notes" rows={3} value={form.notes} onChange={(e) => update({ notes: e.target.value })} data-testid="machine-form-notes" />
            </div>
            <div className="md:col-span-2 flex items-center justify-between rounded-sm border border-border bg-card p-3">
              <div>
                <div className="text-sm font-medium">Active</div>
                <div className="text-xs text-muted-foreground">Inactive machines are hidden from the default list.</div>
              </div>
              <Switch checked={form.active} onCheckedChange={(v) => update({ active: v })} data-testid="machine-form-active" />
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-md">
          <CardHeader>
            <CardTitle className="text-sm font-medium flex items-center gap-2"><FileText className="h-4 w-4 text-primary" /> Machine-specific manuals</CardTitle>
          </CardHeader>
          <CardContent>
            <FileUploader
              files={manuals}
              onAttach={handleAttach}
              onDetach={handleDetach}
              testIdPrefix="machine-manuals"
              emptyLabel="No manuals attached to this machine."
            />
          </CardContent>
        </Card>

        <div className="flex items-center justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => navigate(cancelTo)} data-testid="machine-form-cancel">Cancel</Button>
          <Button type="submit" disabled={saving || !form.site_id || !form.machine_type_id} data-testid="machine-form-submit"><Save className="h-4 w-4" />{saving ? "Saving…" : isEdit ? "Save changes" : "Create machine"}</Button>
        </div>
      </form>
    </div>
  );
}
